/**
 * ComparisonDetails — full property metadata side-by-side. Same grid
 * layout as ComparisonOverview but with a longer set of rows pulled
 * straight from the EvaluationSite model.
 *
 * Lease terms is `Mixed` in the model; we render it as a JSON-ish
 * pretty string when present, dash otherwise. Frontage / parking /
 * coTenants / condition are short free-text fields.
 *
 * Linear: MYD-217 (F2).
 */

import ComparisonRowLabel from './ComparisonRowLabel'
import ComparisonSiteHeader from './ComparisonSiteHeader'
import { renderDash, gridTemplate } from './comparisonShared'

interface ComparisonDetailsProps {
	sites: any[]
}

const fmtSqft = (s: any) => (s.sqft ? `${Number(s.sqft).toLocaleString()} sqft` : null)
const fmtTi = (n: any) =>
	typeof n === 'number' ? `$${Math.round(n).toLocaleString()}` : null

const fmtLeaseTerms = (lt: any) => {
	if (lt == null) return null
	if (typeof lt === 'string') return lt
	if (typeof lt === 'object') {
		const parts: string[] = []
		if (lt.term) parts.push(`${lt.term} yr`)
		if (lt.baseRent) parts.push(`$${Number(lt.baseRent).toLocaleString()} base`)
		if (lt.escalations) parts.push(`${lt.escalations} esc.`)
		if (parts.length === 0) {
			try {
				return JSON.stringify(lt)
			} catch {
				return null
			}
		}
		return parts.join(' · ')
	}
	return String(lt)
}

const ComparisonDetails = ({ sites }: ComparisonDetailsProps) => {
	const rows: { label: string; render: (site: any) => React.ReactNode }[] = [
		{ label: 'Property type', render: (s) => renderDash(s.propertyType) },
		{ label: 'Sqft', render: (s) => renderDash(fmtSqft(s)) },
		{ label: 'Frontage', render: (s) => renderDash(s.frontage) },
		{ label: 'Parking', render: (s) => renderDash(s.parking) },
		{ label: 'Co-tenants', render: (s) => renderDash(s.coTenants) },
		{ label: 'Condition', render: (s) => renderDash(s.condition) },
		{ label: 'Lease terms', render: (s) => renderDash(fmtLeaseTerms(s.leaseTerms)) },
		{ label: 'TI allowance', render: (s) => renderDash(fmtTi(s.tiAllowance)) },
	]

	return (
		<div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
			<div
				className="grid"
				style={{ gridTemplateColumns: gridTemplate(sites.length), minWidth: '100%' }}
			>
				<div className="bg-gray-50 border-b border-gray-200 px-3 py-2 sticky left-0 z-10" />
				{sites.map((site) => (
					<ComparisonSiteHeader key={site._id} site={site} />
				))}

				{rows.map((row, idx) => (
					<div className="contents" key={row.label}>
						<ComparisonRowLabel
							label={row.label}
							className={idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'}
						/>
						{sites.map((site) => (
							<div
								key={site._id}
								className={`px-3 py-2 text-sm text-gray-700 border-t border-gray-100 ${
									idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'
								}`}
							>
								{row.render(site)}
							</div>
						))}
					</div>
				))}
			</div>
		</div>
	)
}

export default ComparisonDetails
